export type WidgetThemeMode = 'system' | 'light' | 'dark';
export type WidgetFontSize = 'default' | 'compact' | 'comfortable';

export type WidgetColors = {
  brand_color: string;
  primary_text_light: string;
  secondary_text_light: string;
  primary_text_dark: string;
  secondary_text_dark: string;
};

export type WidgetTypography = {
  font: string;
  size: WidgetFontSize;
};

export type ChatFeatureFlags = {
  is_media_file_sharing: boolean;
  is_edit_message: boolean;
  is_user_typing_indicator: boolean;
  is_user_presence: boolean;
  is_message_reaction: boolean;
  is_public_group: boolean;
  is_delete_chat: boolean;
  is_delete_conversation: boolean;
  is_read_and_delivered_receipts: boolean;
  is_mentions: boolean;
  is_thread: boolean;
  is_quote_reply: boolean;
  is_private_and_protected_group: boolean;
  is_search_message: boolean;
  is_block_user: boolean;
  is_mute_user: boolean;
  is_ban_user: boolean;
  is_voice_message: boolean;
  is_pin_message: boolean;
  is_polls: boolean;
};

export type CallFeatureFlags = {
  is_one_to_one_voice_calls: boolean;
  is_group_voice_calls: boolean;
  is_one_to_one_video_calls: boolean;
  is_group_video_calls: boolean;
  is_invite_users: boolean;
  is_shared_screen: boolean;
  is_remove_participants: boolean;
  is_microphone_control: boolean;
  is_flexible_join_schedule_calls: boolean;
  is_schedule_call_by_slot: boolean;
  is_schedule_calls: boolean;
  is_make_host_call: boolean;
};

export type BotFeatureFlags = {
  is_add_new_session: boolean;
  is_history: boolean;
  is_request_agent: boolean;
};

export type Tab = {
  users_tab: boolean;
  group_tab: boolean;
  call_history_tab: boolean;
  schedule_call_history_tab: boolean;
};

export type TabId = keyof Tab;

export type WidgetBuilderConfig = {
  varient_id: string;
  platform_name: string;
  platform_type: string;
  is_bot_varient: boolean;
  theme: WidgetThemeMode;
  colors: WidgetColors;
  typography: WidgetTypography;
  with_sidebar: boolean;
  tabs: Tab;
  chat_feature: ChatFeatureFlags;
  call_feature: CallFeatureFlags;
  bot_features: BotFeatureFlags;
};

export const DEFAULT_WIDGET_COLORS: WidgetColors = {
  brand_color: '#007aff',
  primary_text_light: '#111827',
  secondary_text_light: '#6b7280',
  primary_text_dark: '#FFFFFF',
  secondary_text_dark: '#989898',
};

export const DEFAULT_CHAT_FEATURES: ChatFeatureFlags = {
  is_media_file_sharing: true,
  is_edit_message: true,
  is_user_typing_indicator: true,
  is_user_presence: true,
  is_message_reaction: true,
  is_public_group: true,
  is_delete_chat: true,
  is_delete_conversation: true,
  is_read_and_delivered_receipts: true,
  is_mentions: true,
  is_thread: true,
  is_quote_reply: true,
  is_private_and_protected_group: true,
  is_search_message: true,
  is_block_user: true,
  is_mute_user: true,
  is_ban_user: true,
  is_voice_message: true,
  is_pin_message: true,
  is_polls: true,
};

export const DEFAULT_CALL_FEATURES: CallFeatureFlags = {
  is_one_to_one_voice_calls: true,
  is_group_voice_calls: true,
  is_one_to_one_video_calls: true,
  is_group_video_calls: true,
  is_invite_users: true,
  is_shared_screen: true,
  is_remove_participants: true,
  is_microphone_control: true,
  is_flexible_join_schedule_calls: true,
  is_schedule_call_by_slot: true,
  is_schedule_calls: true,
  is_make_host_call: true,
};

export const DEFAULT_BOT_FEATURES: BotFeatureFlags = {
  is_add_new_session: true,
  is_history: true,
  is_request_agent: true,
};

export const DEFAULT_TABS: Tab = {
  users_tab: true,
  group_tab: true,
  call_history_tab: true,
  schedule_call_history_tab: true,
};

export const CHAT_FEATURE_LABELS: Array<{ key: keyof ChatFeatureFlags; label: string; description: string }> = [
  { key: 'is_media_file_sharing', label: 'Media & File Sharing', description: 'Share images, videos, and files in chat.' },
  { key: 'is_user_typing_indicator', label: 'Typing Indicator', description: 'Show when someone is typing.' },
  { key: 'is_user_presence', label: 'User Presence', description: 'Show online / offline status.' },
  { key: 'is_message_reaction', label: 'Message Reactions', description: 'React to messages with emoji.' },
  { key: 'is_voice_message', label: 'Voice Messages', description: 'Send voice notes in conversations.' },
  { key: 'is_read_and_delivered_receipts', label: 'Read Receipts', description: 'Show delivered and read ticks.' },
  { key: 'is_search_message', label: 'Search Messages', description: 'Search across conversation history.' },
  { key: 'is_quote_reply', label: 'Quote Reply', description: 'Reply by quoting a message.' },
  { key: 'is_thread', label: 'Threads', description: 'Start threaded replies on a message.' },
  { key: 'is_public_group', label: 'Public Groups', description: 'Allow open group conversations.' },
  { key: 'is_delete_chat', label: 'Delete Chat', description: 'Delete a chat.' },
  { key: 'is_delete_conversation', label: 'Delete Conversation', description: 'Delete a conversation.' },
  { key: 'is_mentions', label: 'Mentions', description: 'Mention users in messages.' },
  { key: 'is_private_and_protected_group', label: 'Private & Protected Groups', description: 'Create private and protected groups.' },
  { key: 'is_block_user', label: 'Block User', description: 'Block a user from the chat.' },
  { key: 'is_ban_user', label: 'Ban User', description: 'Ban a user from the chat.' },
];

export const CALL_FEATURE_LABELS: Array<{ key: keyof CallFeatureFlags; label: string; description: string }> = [
  { key: 'is_one_to_one_voice_calls', label: '1:1 Voice Calls', description: 'Start private voice calls.' },
  { key: 'is_one_to_one_video_calls', label: '1:1 Video Calls', description: 'Start private video calls.' },
  { key: 'is_group_voice_calls', label: 'Group Voice Calls', description: 'Start group voice calls.' },
  { key: 'is_group_video_calls', label: 'Group Video Calls', description: 'Start group video calls.' },
  { key: 'is_shared_screen', label: 'Screen Share', description: 'Share screen during calls.' },
  { key: 'is_invite_users', label: 'Invite Users', description: 'Invite others into an ongoing call.' },
  { key: 'is_schedule_calls', label: 'Schedule Calls', description: 'Schedule calls in advance.' },
  { key: 'is_remove_participants', label: 'Remove Participants', description: 'Remove participants from a call.' },
  { key: 'is_microphone_control', label: 'Microphone Control', description: 'Control the microphone during a call.' },
  { key: 'is_flexible_join_schedule_calls', label: 'Flexible Join Schedule Calls', description: 'Allow users to join a call at any time.' },
  { key: 'is_schedule_call_by_slot', label: 'Schedule Call by Slot', description: 'Schedule calls at specific times.' },
  { key: 'is_make_host_call', label: 'Make Call Host', description: 'Make the host of the call.' },
];

export const BOT_FEATURE_LABELS: Array<{ key: keyof BotFeatureFlags; label: string; description: string }> = [
  { key: 'is_add_new_session', label: 'New Session', description: 'Allow users to start a fresh bot session.' },
  { key: 'is_history', label: 'Chat History', description: 'Show previous bot conversation history.' },
  { key: 'is_request_agent', label: 'Request Agent', description: 'Let users request a human agent from the bot.' },
];

export const TAB_LABELS: Array<{ id: TabId; label: string; description: string }> = [
  { id: 'users_tab', label: 'Users', description: 'Access and connect with users.' },
  { id: 'group_tab', label: 'Groups', description: 'Browse and join group chats.' },
  { id: 'call_history_tab', label: 'Call History', description: 'View recent call history and details.' },
  { id: 'schedule_call_history_tab', label: 'Schedule Call History', description: 'View scheduled call history.' },
];

export const FONT_OPTIONS = ['Poppins', 'Roboto', 'Inter', 'Open Sans', 'Nunito'];

export const resolveResolvedTheme = (theme: WidgetThemeMode): 'light' | 'dark' => {
  if (theme === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }
  return theme;
};
